import React, { useState, useEffect } from 'react';

const SimpleImageViewer = ({ images, open, currentIndex = 0, onClose }) => {
  const [selectedIndex, setSelectedIndex] = useState(currentIndex);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    setSelectedIndex(currentIndex);
  }, [currentIndex]);

  useEffect(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, [selectedIndex]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!open) return;
      switch (e.key) {
        case 'ArrowLeft':
          handlePrevious();
          break;
        case 'ArrowRight':
          handleNext();
          break;
        case 'Escape':
          onClose();
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, selectedIndex, images.length, onClose]);

  const handleNext = () => {
    if (selectedIndex < images.length - 1) {
      setSelectedIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (selectedIndex > 0) {
      setSelectedIndex(prev => prev - 1);
    }
  };

  const handleImageClick = (e) => {
    if (zoom === 1) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width - 0.5) * -400;
      const y = ((e.clientY - rect.top) / rect.height - 0.5) * -400;
      setZoom(2.5);
      setPosition({ x, y });
    } else {
      setZoom(1);
      setPosition({ x: 0, y: 0 });
    }
  };

  const handleMouseMove = (e) => {
    if (zoom > 1) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width - 0.5) * -500;
      const y = ((e.clientY - rect.top) / rect.height - 0.5) * -500;
      setPosition({ x, y });
    }
  };

  if (!open || !images || images.length === 0) return null;

  const currentImage = images[selectedIndex];
  const imageSrc = typeof currentImage === 'string'
    ? currentImage
    : currentImage.src || currentImage.url || '';

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden"
      style={{ backgroundColor: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(8px)' }}
      onMouseMove={handleMouseMove}
      onMouseUp={() => {}}
      onMouseLeave={() => {}}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-5 right-5 z-10 p-2 rounded-full shadow-md transition-transform hover:scale-110"
        style={{ backgroundColor: 'rgba(255,255,255,0.9)' }}
        aria-label="Close"
      >
        <span className="material-symbols-outlined text-on-surface">close</span>
      </button>

      {/* Previous button */}
      {selectedIndex > 0 && (
        <button
          onClick={handlePrevious}
          className="absolute left-5 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full shadow-md transition-transform hover:scale-110"
          style={{ backgroundColor: 'rgba(255,255,255,0.9)' }}
          aria-label="Previous image"
        >
          <span className="material-symbols-outlined text-on-surface" style={{ fontSize: 32 }}>chevron_left</span>
        </button>
      )}

      {/* Next button */}
      {selectedIndex < images.length - 1 && (
        <button
          onClick={handleNext}
          className="absolute right-5 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full shadow-md transition-transform hover:scale-110"
          style={{ backgroundColor: 'rgba(255,255,255,0.9)' }}
          aria-label="Next image"
        >
          <span className="material-symbols-outlined text-on-surface" style={{ fontSize: 32 }}>chevron_right</span>
        </button>
      )}

      {/* Image counter */}
      {images.length > 1 && (
        <div
          className="absolute bottom-5 right-5 z-10 px-3 py-1.5 rounded shadow-md font-medium text-sm text-on-surface"
          style={{ backgroundColor: 'rgba(255,255,255,0.9)' }}
        >
          {selectedIndex + 1} / {images.length}
        </div>
      )}

      {/* Main image */}
      <img
        src={imageSrc}
        alt={`Image ${selectedIndex + 1}`}
        onClick={handleImageClick}
        style={{
          width: '95vw',
          height: '95vh',
          objectFit: 'contain',
          cursor: zoom > 1 ? 'move' : 'zoom-in',
          transform: `scale(${zoom}) translate(${position.x}px, ${position.y}px)`,
          transition: zoom > 1 ? 'none' : 'transform 0.3s ease',
          userSelect: 'none',
          WebkitUserDrag: 'none',
          filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))',
        }}
      />
    </div>
  );
};

export default SimpleImageViewer;
